import type { AdminLang } from '@klukka/core';
import { ta } from '@klukka/core';
import type { CSSProperties, ReactNode } from 'react';

export type Tr = (k: Parameters<typeof ta>[1]) => string;

export function makeTr(lang: AdminLang): Tr {
  return (k) => ta(lang, k);
}

export function KPI({ label, value, sub, accent }: {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div className="kpi">
      <div className="kpi-l">{label}</div>
      <div className="kpi-v" style={accent ? { color: 'var(--primary)' } : undefined}>{value}</div>
      {sub ? <div className="kpi-s">{sub}</div> : null}
    </div>
  );
}

export function Panel({ title, children, style }: { title?: ReactNode; children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="panel2" style={style}>
      {title ? <div className="p-title">{title}</div> : null}
      {children}
    </div>
  );
}

/**
 * A settings input. Numeric by default because most of them are rates and
 * thresholds; pass `type` for text and dates.
 */
export function Fld<T extends string | number>({ label, value, onChange, width, suffix, type, placeholder }: {
  label: ReactNode;
  value: T;
  onChange: (v: T) => void;
  width?: string;
  suffix?: string;
  type?: 'text' | 'number' | 'date';
  placeholder?: string;
}) {
  const numeric = !type || type === 'number';
  return (
    <label className="s-fld" style={width ? { width } : undefined}>
      {label}
      <span className="s-inp-wrap">
        <input
          className="inp"
          type={type ?? 'number'}
          step="any"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange((numeric ? parseFloat(e.target.value) || 0 : e.target.value) as T)}
        />
        {suffix ? <em>{suffix}</em> : null}
      </span>
    </label>
  );
}

export function Select({ label, value, onChange, options, width, hint }: {
  label: ReactNode;
  value: string | number;
  onChange: (v: string) => void;
  options: Array<{ value: string | number; label: string }>;
  width?: string;
  hint?: ReactNode;
}) {
  return (
    <label className="s-fld" style={width ? { width } : undefined}>
      {label}
      <select className="inp" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {hint ? <span className="dimtx" style={{ fontWeight: 400, fontSize: 11 }}>{hint}</span> : null}
    </label>
  );
}

const STATUS_COLOR: Record<string, string> = {
  pending: 'var(--warn)', approved: 'var(--ok)', rejected: 'var(--danger)', submitted: 'var(--ink2)',
};
const STATUS_TEXT: Record<AdminLang, Record<string, string>> = {
  is: { pending: 'Í bið', approved: 'Samþykkt', rejected: 'Hafnað', submitted: 'Sent' },
  en: { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', submitted: 'Submitted' },
};

/** Status chip using the admin vocabulary rather than the worker's. */
export function AdminChip({ status, lang }: { status: string; lang: AdminLang }) {
  const color = STATUS_COLOR[status] ?? 'var(--ink3)';
  return <span className="chip" style={{ borderColor: color, color }}>{STATUS_TEXT[lang][status] ?? status}</span>;
}
